<template>
  <div class="mt-0">
    <!-- 进度条属性 -->
    <!-- 角度范围 -->
    <div v-if="shape.attrs['progressBarType'] == 'arc'">
      <!-- 为兼容1.5 -->
      <template
        v-if="
          shape.attrs.barWidth !== undefined &&
          shape.attrs.barRadius !== undefined
        "
      >
        <AttrProgressBarRadius :shape="shape" :disabled="!!attrDisabled" />
      </template>
      <template v-else>
        <AttrInputNumber
          label="进度条半径"
          :value="shape.attrs.innerRingPercent"
          :disabled="!!attrDisabled"
          :min="0"
          :max="100"
          addon-after="%"
          @change="(val:number) => setAttrs({'innerRingPercent':val})"
        />
      </template>

      <div class="flex justify-between mt-2 px-5 py-2">
        <div
          v-for="item in progressRangeItems"
          :key="item.icon"
          :class="{
            'opacity-30 cursor-not-allowed': !!attrDisabled
          }"
          @click="
            () =>
              !attrDisabled &&
              setAttrs({
                angleStart: item.start,
                angleEnd: item.end
              })
          "
        >
          <a-tooltip :title="item.start + '°~' + item.end + '°'">
            <Icon :icon="item.icon" :width="20" :height="20" fill="none" />
          </a-tooltip>
        </div>
      </div>
      <div class="grid grid-cols-2 gap-2 my-2 mt-3">
        <AttrInputNumber
          v-for="item in angelInputItems"
          :key="item.key"
          :label="item.label"
          :value="shape.attrs[item.key]"
          :disabled="!!attrDisabled"
          addon-after="°"
          :width="56"
          @change="(val:number) => setAttrs({[item.key]:val})"
        />
      </div>
    </div>
    <div class="flex gap-2 mt-2.5">
      <!-- 端点 -->
      <AttrSelect
        label="端点"
        :value="shape.attrs['endingStyle']"
        :options="[
          {
            value: 'normal',
            label: '平角'
          },
          {
            value: 'round',
            label: '圆角'
          }
        ]"
        :disabled="!!attrDisabled"
        :width="90"
        @change="(value: any)=>setAttrs({'endingStyle': value})"
      />
      <!-- 指示器半径 -->
      <AttrInputNumber
        v-if="shape.attrs.progressBarType === 'arc'"
        label="指示器半径"
        palceholder="请输入"
        :value="shape.attrs['indicatorRadius'] ?? 0"
        :disabled="!!attrDisabled"
        :min="0"
        :width="70"
        @change="(val:number) => setAttrs({['indicatorRadius']:val})"
      />
    </div>
    <AttrSelect
      class="mt-2.5"
      label="增长方向"
      :value="growDirection"
      :disabled="!!attrDisabled"
      :options="dirOptions"
      @change="(value: any)=>setAttrs({'growDirection': value})"
    />
  </div>
</template>
<script lang="ts" setup>
import { Shape } from '@/services/shape';
import { ProgressBarAttribute } from '@/services/shape/attributes/progress-bar';
import { useEditorStore } from '@/store/use-editor-store';
import { PropType, computed } from 'vue';

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  }
});

const editorStore = useEditorStore();
const attrDisabled = computed(() => editorStore.attrDisabled);

const dirOptions = computed<
  {
    label: string;
    value: typeof props.shape.attrs.growDirection;
  }[]
>(() => {
  if (props.shape.attrs.progressBarType == 'arc') {
    return [
      { label: '顺时针', value: 'positive' },
      { label: '逆时针', value: 'negative' }
    ];
  } else {
    return [
      { label: '从左到右', value: 'to-right' },
      { label: '从右到左', value: 'to-left' },
      { label: '从上到下', value: 'to-down' },
      { label: '从下到上', value: 'to-up' }
    ];
  }
});

const growDirection = computed(() => {
  const {
    angleStart = 0,
    angleEnd = 0,
    progressBarType = 'arc',
    growDirection = 'to-right'
  } = props.shape.attrs;
  if (progressBarType == 'line') {
    return growDirection;
  } else {
    return angleEnd >= angleStart ? 'positive' : 'negative';
  }
});

const progressRangeItems: {
  start: number;
  end: number;
  icon: string;
}[] = [
  {
    icon: 'progress_range1.svg',
    start: -45,
    end: 45
  },
  {
    icon: 'progress_range2.svg',
    start: 180 - 45,
    end: 180 + 45
  },
  {
    icon: 'progress_range3.svg',
    start: 180 + 45,
    end: 360 - 45
  },
  {
    icon: 'progress_range4.svg',
    start: 90 - 45,
    end: 90 + 45
  },
  {
    icon: 'progress_range5.svg',
    start: 90,
    end: 270
  },
  {
    icon: 'progress_range6.svg',
    start: -90,
    end: 90
  }
];

const angelInputItems: { label: string; key: 'angleStart' | 'angleEnd' }[] = [
  {
    label: '起始角度',
    key: 'angleStart'
  },
  {
    label: '结束角度',
    key: 'angleEnd'
  }
];

const setAttrs = (data: Partial<ProgressBarAttribute>) => {
  const { angleEnd = 0, angleStart = 0, progressBarType } = props.shape.attrs;
  const growDirection = data['growDirection'];
  if (growDirection) {
    if (progressBarType == 'arc') {
      const min = Math.min(angleStart, angleEnd);
      const max = Math.max(angleStart, angleEnd);
      data['angleStart'] = growDirection == 'positive' ? min : max;
      data['angleEnd'] = growDirection == 'positive' ? max : min;
    }
  }
  props.shape?.setAttrs(data);
};
</script>
